import {GetAnalysisPipelineStep} from 'analysis-tool/AnalysisTypes';
import {
  InputTableColumnReference,
  RESTIdentifiedWarehouseParameter,
  DataFrameCreationMethod,
} from 'analysis-tool/data-frame-creation/DataFrameCreationTypes';
import {
  AnalysisMainViewItem,
  AnalysisMainViewItemType,
  CreationOptionalIds,
  StepInitializationAnalysisMainViewItem,
} from 'analysis-tool/ui/main-view/MainViewTypes';
import {trackEvent} from 'analytics/Analytics';
import {assert} from 'Asserts';
import {ActionOrigin, ANALYSIS_TOOL_ANALYTICS_ACTIONS, ActionMethod} from 'constants/AnalyticsConstants';
import {DataFrameColumnDef, DataFrameSpec} from 'data-frames/DataFrameTypes';
import findAndAssertColumn from 'data-frames/util/findAndAssertColumn';
import {getParameterConfigsSpecFromReadShape} from 'insights/analytics-dashboards/RESTAnalyticsDashboardTypes';
import {AnalyticsParameterValue} from 'insights/parameters/__generated/AnalyticsParameterValue';
import {restGetQueryParameterConfigs} from 'insights/parameters/data/getQueryParameterConfigs';
import {convertGQLToRESTParameterValue} from 'insights/parameters/data/ParameterGQLRESTConverters';
import {restGetValueJsonForConfig} from 'insights/parameters/data/ValueGetters';
import {
  RESTAnalyticsParameterConfig,
  RESTAnalyticsParameterValue,
} from 'insights/parameters/RESTParameterTypes';
import pickDefined from 'util/pickDefined';

import {AnalyticsParameterConfigGraphQL} from '../../../graphql-fragments/insights/__generated/AnalyticsParameterConfigGraphQL';
import {PersistedRunQueryData} from './CreateDataFrameFromAnalyticsBlockViewSkeleton';
import {
  DATA_FRAME_CREATION_METHOD_OPTIONS,
  ORDERED_DATA_FRAME_CREATION_METHODS,
} from './DataFrameCreationConstants';

export const MISSING_COLUMN_ERROR_MESSAGE =
  'Expected columnReference to reference at least one dataFrameSpec from input';

export function findAndAssertColumnFromSpecs(
  dataFrameSpecs: Array<DataFrameSpec>,
  columnReference: InputTableColumnReference
): DataFrameColumnDef {
  const selectedDataFrameSpec = dataFrameSpecs.find(({name}) => columnReference.dataFrameName === name);

  assert(selectedDataFrameSpec != null, MISSING_COLUMN_ERROR_MESSAGE, {dataFrameSpecs, columnReference});

  return findAndAssertColumn(selectedDataFrameSpec, columnReference.columnSystemName);
}

export function convertRESTToIdentifiedWarehouseParameters(
  parameterConfigs: Array<RESTAnalyticsParameterConfig>,
  parameterValues: Array<RESTAnalyticsParameterValue>
): Array<RESTIdentifiedWarehouseParameter> {
  return parameterConfigs.map((config) => ({
    config: pickDefined(config, 'id', 'type', 'systemName', 'isMulti'),
    valueJson: config.id ? restGetValueJsonForConfig(parameterValues, config.id) : null,
  }));
}

export function getEnabledDatasetCreationMethods(
  selectedItem?: AnalysisMainViewItem
): Array<DataFrameCreationMethod> {
  const creationMethods = ORDERED_DATA_FRAME_CREATION_METHODS.filter((datasetCreationMethod) =>
    DATA_FRAME_CREATION_METHOD_OPTIONS[datasetCreationMethod].isEnabled()
  );
  if (selectedItem && isReplacingTable(selectedItem)) {
    return creationMethods.filter((method) => DATA_FRAME_CREATION_METHOD_OPTIONS[method].canReplace);
  }
  return creationMethods;
}

export function getAddDataFrameAnalysisViewItem(): StepInitializationAnalysisMainViewItem {
  const enabledDataFrameCreationMethods = getEnabledDatasetCreationMethods();

  assert(
    enabledDataFrameCreationMethods.length > 0,
    'Expected at least 1 data frame creation method to be enabled'
  );

  return enabledDataFrameCreationMethods.length > 1
    ? {type: AnalysisMainViewItemType.CHOOSE_DATASET_CREATION_METHOD}
    : DATA_FRAME_CREATION_METHOD_OPTIONS[enabledDataFrameCreationMethods[0]].initializationViewItem;
}

export function trackSelectedDataFrameCreationMethodEvent(
  origin:
    | ActionOrigin.ANALYSIS_TOOL_ANALYSIS_PAGE_DATA_FRAME_SIDEPANEL
    | ActionOrigin.ANALYSIS_TOOL_CHOOSE_DATA_FRAME_CREATION_VIEW
    | ActionOrigin.ANALYSIS_TOOL_EMPTY_ANALYSIS_PAGE
    | ActionOrigin.ANALYSIS_TOOL_HEADER,
  analysisId: string,
  numOptionsAvailable: number,
  creationMethod: DataFrameCreationMethod
): void {
  trackEvent(ANALYSIS_TOOL_ANALYTICS_ACTIONS.SELECTED_DATA_FRAME_CREATION_METHOD, {
    action_method: ActionMethod.BUTTON_CLICK,
    action_origin: origin,
    pipeline_id: analysisId,
    num_options_available: numOptionsAvailable,
    option: DATA_FRAME_CREATION_METHOD_OPTIONS[creationMethod].analyticsEventName,
  });
}

function hasStepIdToReplace(item: AnalysisMainViewItem): item is AnalysisMainViewItem & CreationOptionalIds {
  return (item as {type: AnalysisMainViewItemType; stepIdToReplace: string}).hasOwnProperty(
    'stepIdToReplace'
  );
}

export function isReplacingTable(item: AnalysisMainViewItem): boolean {
  return hasStepIdToReplace(item) && item.stepIdToReplace != null;
}

export function getSharedDataFrameCreationMethodProps(item: AnalysisMainViewItem): {
  finalizeButtonText: string;
} {
  if (isReplacingTable(item)) {
    return {
      finalizeButtonText: 'Replace table',
    };
  }
  return {
    finalizeButtonText: 'Add table',
  };
}

export function openSelectedItemForNewStep(
  newStep: GetAnalysisPipelineStep,
  onCloseStepInitializationView: (newSelectedItem: AnalysisMainViewItem) => void
): void {
  const newSelectedItem: AnalysisMainViewItem = {
    type: AnalysisMainViewItemType.DATA_VIEW,
    viewReference: {
      stepId: newStep.id,
      outputReference: {stepDataName: newStep.outputStepDatas[0].name, rawDataIndex: 0},
    },
  };
  onCloseStepInitializationView(newSelectedItem);
}

/**
 * Convert GraphQL parameter configs and values into the format needed for INITIALIZE_DATASET step config persistence.
 */
export function getPersistedRunQueryData({
  parameterConfigs,
  parameterValues,
  sqlQuery,
}: {
  parameterConfigs: Array<AnalyticsParameterConfigGraphQL>;
  parameterValues: Array<AnalyticsParameterValue>;
  sqlQuery: string;
}): PersistedRunQueryData {
  const restParameterConfigs = getParameterConfigsSpecFromReadShape(
    parameterConfigs
  ) as Array<RESTAnalyticsParameterConfig>;
  const restParameterValues = parameterValues.map((value) => convertGQLToRESTParameterValue(value));

  return {
    parameterizedSQLQuery: sqlQuery,
    parameters: restGetQueryParameterConfigs(
      sqlQuery,
      convertRESTToIdentifiedWarehouseParameters(restParameterConfigs, restParameterValues)
    ),
  };
}
